import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { format } from "date-fns";
import { toast } from "sonner";
import { Pencil, Bell, Receipt, Euro, FileDown, XCircle } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { euro } from "@/lib/rinks";

const STATUSES = ["submitted", "confirmed", "reminder sent", "invoice sent", "paid", "cancelled"];

const STATUS_BADGE = {
  submitted: "bg-amber-300", confirmed: "bg-sky-300", "reminder sent": "bg-violet-300", "invoice sent": "bg-teal-300",
  paid: "bg-emerald-300", cancelled: "bg-red-300",
};

const ICON_BTN = "bg-white border-2 border-slate-900 p-1.5 hover:bg-sky-100 shadow-[2px_2px_0px_0px_#0f172a] transition-colors inline-flex items-center justify-center";

const schoolDays = (cfg) => {
  if (!cfg) return [];
  const out = [];
  for (const w of cfg.windows) {
    for (let d = new Date(`${w.start}T00:00:00`); d <= new Date(`${w.end}T00:00:00`); d = new Date(d.getTime() + 86400000)) {
      if (d.getDay() >= 1 && d.getDay() <= 5) out.push(format(d, "yyyy-MM-dd"));
    }
  }
  return out;
};

export default function SchoolsAdminPage() {
  const { user } = useAuth();
  const canEdit = user.role !== "viewer";
  const [searchParams] = useSearchParams();
  const rinkParam = searchParams.get("rink");
  const [rink, setRink] = useState(user.role === "staff" ? user.rink : (["clonmel", "sligo"].includes(rinkParam) ? rinkParam : "clonmel"));
  useEffect(() => {
    if (user.role !== "staff" && ["clonmel", "sligo"].includes(rinkParam)) setRink(rinkParam);
  }, [rinkParam]);
  const [cfg, setCfg] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [waitlist, setWaitlist] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [csvText, setCsvText] = useState("");
  const [showImport, setShowImport] = useState(false);
  const [includeCancelled, setIncludeCancelled] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [preview, setPreview] = useState(null);

  const days = useMemo(() => schoolDays(cfg), [cfg]);
  const times = cfg?.times || [];
  const price = cfg?.price_per_child ?? 14;

  const load = useCallback(async () => {
    setLoading(true);
    setSelected(new Set());
    try {
      const [c, b, w, bl, st] = await Promise.all([
        api.get("/school/config", { params: { rink } }),
        api.get("/school/admin/bookings", { params: { rink } }),
        api.get("/school/admin/waitlist", { params: { rink } }),
        api.get("/school/admin/slot-blocks", { params: { rink } }),
        api.get("/school/admin/stats", { params: { rink } }),
      ]);
      setCfg(c.data);
      setBookings(b.data);
      setWaitlist(w.data);
      setBlocks(bl.data);
      setStats(st.data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, [rink]);

  useEffect(() => { load(); }, [load]);

  const act = async (fn, okMsg) => {
    try {
      const r = await fn();
      if (okMsg) toast.success(typeof okMsg === "function" ? okMsg(r.data) : okMsg);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const cancel = (b) => {
    if (!window.confirm(`Cancel booking ${b.reference} (${b.school_name})? The first waitlister will be notified.`)) return;
    act(() => api.delete(`/school/admin/bookings/${b.id}`), "Booking cancelled");
  };

  const downloadCsv = async () => {
    try {
      const { data } = await api.get("/school/admin/export.csv", { params: { rink }, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${rink}-school-bookings.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const importCsv = () => act(async () => {
    const { data } = await api.post("/school/admin/import", { csv_text: csvText }, { params: { rink } });
    toast.success(`Imported: ${data.created} created, ${data.updated} updated, ${data.skipped} skipped`);
  });

  const REQUIRED_EDIT = [["school_name", "School name"], ["teacher_name", "Teacher name"], ["email", "Email"],
    ["phone", "Phone"], ["county", "County"], ["school_address", "School address"], ["age_group", "Age group"], ["status", "Status"]];

  const saveEdit = () => {
    for (const [k, label] of REQUIRED_EDIT) {
      if (!String(editing[k] ?? "").trim()) { toast.error(`${label} is required`); return; }
    }
    if (!(Number(editing.group_size) > 0)) { toast.error("Pupils is required"); return; }
    act(async () => {
      const { id, created_at, updated_at, reference, rink: _r, cancelled, invoice_no, ...fields } = editing;
      await api.patch(`/school/admin/bookings/${id}`, { ...fields, group_size: Number(fields.group_size) });
      setEditing(null);
    }, "Booking updated");
  };

  const toggleSelect = (id) => setSelected((s) => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });

  const openInvoicePreview = async (list) => {
    try {
      let invoice_no;
      if (list.length === 1) {
        const { data } = await api.post(`/school/admin/bookings/${list[0].id}/invoice/prepare`);
        invoice_no = data.invoice_no;
      } else {
        const { data } = await api.post("/school/admin/bookings/invoice/prepare", { booking_ids: list.map((b) => b.id) });
        invoice_no = data.invoice_no;
      }
      setPreview({ type: "invoice", bookings: list, invoice_no });
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const confirmSend = async () => {
    try {
      if (preview.type === "reminder") {
        await api.post(`/school/admin/bookings/${preview.bookings[0].id}/send-reminder`);
        toast.success("Reminder email sent");
      } else if (preview.bookings.length === 1) {
        const { data } = await api.post(`/school/admin/bookings/${preview.bookings[0].id}/send-invoice`);
        toast.success(`Invoice ${data.invoice_no} sent`);
      } else {
        const { data } = await api.post("/school/admin/bookings/invoice/send", { booking_ids: preview.bookings.map((b) => b.id) });
        toast.success(`Merged invoice ${data.invoice_no} sent`);
      }
      setPreview(null);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  // --- block calendar helpers ---
  const blockMap = useMemo(() => {
    const m = {};
    blocks.forEach((b) => { m[`${b.date}|${b.time || "day"}`] = b; });
    return m;
  }, [blocks]);

  const usage = useMemo(() => {
    const m = {};
    bookings.filter((b) => !b.cancelled).forEach((b) => {
      const k = `${b.date}|${b.time}`;
      m[k] = (m[k] || 0) + b.group_size;
    });
    return m;
  }, [bookings]);

  const weeks = useMemo(() => {
    const map = {};
    days.forEach((d) => {
      const dt = new Date(`${d}T00:00:00`);
      const monday = new Date(dt);
      monday.setDate(dt.getDate() - (dt.getDay() - 1));
      const key = format(monday, "yyyy-MM-dd");
      (map[key] = map[key] || {})[dt.getDay()] = d;
    });
    return Object.entries(map).sort(([a], [b]) => (a < b ? -1 : 1));
  }, [days]);

  const toggleSlot = (d, t) => act(async () => {
    const dayBlock = blockMap[`${d}|day`];
    if (dayBlock) { await api.delete(`/school/admin/slot-blocks/${dayBlock.id}`); return; }
    const blk = blockMap[`${d}|${t}`];
    if (blk) await api.delete(`/school/admin/slot-blocks/${blk.id}`);
    else await api.post("/school/admin/slot-blocks", { date: d, time: t }, { params: { rink } });
  });

  const toggleDay = (d) => act(async () => {
    const dayBlock = blockMap[`${d}|day`];
    if (dayBlock) {
      await api.delete(`/school/admin/slot-blocks/${dayBlock.id}`);
    } else {
      await api.post("/school/admin/slot-blocks", { date: d, time: null }, { params: { rink } });
    }
  });

  const visible = includeCancelled ? bookings : bookings.filter((b) => !b.cancelled);
  const selectedBookings = bookings.filter((b) => selected.has(b.id));

  return (
    <div className="max-w-[1400px] mx-auto p-4 md:p-6" data-testid="schools-admin-page">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
        <div>
          <div className="font-mono-tight text-xs uppercase tracking-[0.25em] text-slate-500 mb-2">{cfg?.label || "Schools"}</div>
          <h1 className="font-display text-3xl md:text-4xl uppercase text-slate-900">School Bookings</h1>
          {cfg && (
            <p className="text-sm text-slate-600 mt-2">
              {cfg.windows.map((w) => `${format(new Date(`${w.start}T00:00:00`), "d MMM")} – ${format(new Date(`${w.end}T00:00:00`), "d MMM yy")}`).join(" · ")} · {cfg.times.join(" / ")} · max {cfg.capacity} pupils per slot · €{cfg.price_per_child.toFixed(0)} per child.
            </p>
          )}
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          {user.role !== "staff" && (
            <div className="flex gap-2 bg-slate-100 p-1 border-2 border-slate-900 shadow-[2px_2px_0px_0px_#0f172a]" data-testid="schools-rink-switcher">
              {[["sligo", "Sligo"], ["clonmel", "Clonmel"]].map(([r, label]) => (
                <button key={r} onClick={() => setRink(r)} data-testid={`schools-tab-${r}`}
                  className={rink === r
                    ? "bg-sky-500 text-white border-2 border-slate-900 font-extrabold uppercase text-xs px-4 py-2 tracking-wider shadow-[2px_2px_0px_0px_#0f172a]"
                    : "bg-white text-slate-900 border-2 border-slate-900 hover:bg-slate-200 font-bold uppercase text-xs px-4 py-2 tracking-wider transition-colors"}>
                  {label}
                </button>
              ))}
            </div>
          )}
          <button onClick={downloadCsv} className="bg-white border-2 border-slate-900 font-bold uppercase text-xs px-4 py-2 tracking-wider hover:bg-slate-200 shadow-[2px_2px_0px_0px_#0f172a] transition-colors" data-testid="export-csv-btn">Export CSV</button>
          {user.role === "admin" && (
            <button onClick={() => setShowImport((s) => !s)} className="bg-amber-300 border-2 border-slate-900 font-bold uppercase text-xs px-4 py-2 tracking-wider hover:bg-amber-400 shadow-[2px_2px_0px_0px_#0f172a] transition-colors" data-testid="import-toggle-btn">Import CSV</button>
          )}
        </div>
      </div>

      {stats && (
        <div className="grid grid-cols-3 gap-3 mb-6" data-testid="school-stats">
          {[["Bookings", stats.bookings], ["Pupils", stats.pupils], ["Revenue", euro(stats.revenue)]].map(([label, val]) => (
            <div key={label} className="card-brutal p-4 shadow-[4px_4px_0px_0px_#0f172a]">
              <div className="font-mono-tight text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
              <div className="font-display text-2xl font-extrabold">{val}</div>
            </div>
          ))}
        </div>
      )}

      {showImport && user.role === "admin" && (
        <div className="card-brutal p-4 mb-6 shadow-[4px_4px_0px_0px_#0f172a]" data-testid="import-panel">
          <div className="font-mono-tight text-xs uppercase tracking-[0.25em] text-slate-500 mb-2">Import existing {cfg?.label} school bookings (CSV from the old site)</div>
          <textarea className="input-brutal w-full" rows={4} placeholder="Paste CSV here — columns like reference,date,time,school_name,…" value={csvText} onChange={(e) => setCsvText(e.target.value)} data-testid="import-textarea" />
          <button onClick={importCsv} disabled={!csvText.trim()} className="mt-2 bg-slate-900 text-white border-2 border-slate-900 font-bold uppercase text-xs px-4 py-2 tracking-wider hover:bg-slate-700 disabled:opacity-50 transition-colors" data-testid="import-run-btn">Run import</button>
        </div>
      )}

      <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
        <label className="text-xs font-bold uppercase tracking-wider flex items-center gap-2">
          <input type="checkbox" checked={includeCancelled} onChange={(e) => setIncludeCancelled(e.target.checked)} data-testid="show-cancelled-toggle" />
          Show cancelled
        </label>
        {canEdit && selected.size > 0 && (
          <button onClick={() => openInvoicePreview(selectedBookings)} className="bg-slate-900 text-white border-2 border-slate-900 font-bold uppercase text-xs px-4 py-2 tracking-wider hover:bg-slate-700 shadow-[2px_2px_0px_0px_#0f172a] transition-colors" data-testid="bulk-invoice-btn">
            Preview merged invoice ({selected.size})
          </button>
        )}
      </div>

      <div className="card-brutal shadow-[6px_6px_0px_0px_#0f172a] overflow-x-auto" data-testid="school-bookings-table">
        <table className="w-full table-fixed border-collapse min-w-[1300px]">
          <thead>
            <tr className="bg-slate-900 text-white font-mono-tight uppercase text-[10px] tracking-widest" data-testid="schools-table-head">
              {canEdit && <th className="p-2.5 w-[36px]"></th>}
              <th className="p-2.5 text-left font-bold w-[100px]">Status</th>
              <th className="p-2.5 text-left font-bold w-[120px]">Ref</th>
              <th className="p-2.5 text-left font-bold w-[130px]">When</th>
              <th className="p-2.5 text-left font-bold">School</th>
              <th className="p-2.5 text-left font-bold w-[230px]">Teacher</th>
              <th className="p-2.5 text-left font-bold w-[70px]">Pupils</th>
              <th className="p-2.5 text-left font-bold w-[90px]">Cost</th>
              <th className="p-2.5 text-left font-bold w-[150px]">Age</th>
              <th className="p-2.5 text-left font-bold w-[90px]">PO</th>
              <th className="p-2.5 text-left font-bold w-[200px]">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={11} className="p-8 text-center font-mono-tight text-xs uppercase tracking-widest text-slate-500">Loading…</td></tr>}
            {!loading && visible.length === 0 && <tr><td colSpan={11} className="p-8 text-center font-mono-tight text-xs uppercase tracking-widest text-slate-500" data-testid="no-bookings">No school bookings yet.</td></tr>}
            {!loading && visible.map((b) => (
              <tr key={b.id} className={`border-t-2 border-slate-900 ${b.cancelled ? "bg-red-50 text-slate-500" : "hover:bg-sky-50/60"}`} data-testid={`school-row-${b.reference}`}>
                {canEdit && (
                  <td className="p-2.5 text-center">
                    {!b.cancelled && <input type="checkbox" checked={selected.has(b.id)} onChange={() => toggleSelect(b.id)} data-testid={`select-${b.reference}`} />}
                  </td>
                )}
                <td className="p-2.5"><span className={`${STATUS_BADGE[b.status] || "bg-slate-200"} border-2 border-slate-900 px-1.5 py-0.5 text-[10px] font-bold uppercase`} data-testid={`status-${b.reference}`}>{b.status}</span></td>
                <td className="p-2.5 text-xs font-mono-tight font-bold">
                  {b.reference}
                  {b.invoice_no && <div className="text-slate-500 font-normal" data-testid={`invoice-no-${b.reference}`}>{b.invoice_no}</div>}
                </td>
                <td className="p-2.5 text-sm font-bold">{format(new Date(`${b.date}T00:00:00`), "EEE d MMM")} · {b.time}</td>
                <td className="p-2.5 text-sm">{b.school_name}<div className="text-xs text-slate-500">{b.county}</div></td>
                <td className="p-2.5 text-sm">{b.teacher_name}<div className="text-xs text-slate-500 break-all">{b.email}<br />{b.phone}</div></td>
                <td className="p-2.5 text-sm font-bold">{b.group_size}</td>
                <td className="p-2.5 text-sm font-bold">{euro(b.group_size * price)}</td>
                <td className="p-2.5 text-xs">{b.age_group}</td>
                <td className="p-2.5 text-xs">{b.po_number || "—"}</td>
                <td className="p-2.5 whitespace-nowrap">
                  <div className="flex items-center gap-1.5">
                    <button onClick={() => setEditing({ ...b })} title="Edit booking" className={ICON_BTN} data-testid={`edit-${b.reference}`}><Pencil className="w-4 h-4" /></button>
                    {canEdit && !b.cancelled && (
                      <>
                        <button onClick={() => setPreview({ type: "reminder", bookings: [b] })} title="Preview reminder email before sending" className={ICON_BTN} data-testid={`reminder-${b.reference}`}><Bell className="w-4 h-4" /></button>
                        <button onClick={() => openInvoicePreview([b])} title="Preview invoice PDF before sending" className={ICON_BTN} data-testid={`invoice-${b.reference}`}><Receipt className="w-4 h-4" /></button>
                        <button onClick={() => act(() => api.post(`/school/admin/bookings/${b.id}/toggle-paid`), (d) => `Marked ${d.status} (${d.updated} booking${d.updated > 1 ? "s" : ""})`)} title={b.status === "paid" ? "Mark invoice group unpaid" : "Mark invoice group paid"} className={`${ICON_BTN} ${b.status === "paid" ? "!bg-emerald-300" : ""}`} data-testid={`toggle-paid-${b.reference}`}><Euro className="w-4 h-4" /></button>
                        {b.invoice_no && (
                          <a href={`${process.env.REACT_APP_BACKEND_URL}/api/school/invoice/${b.invoice_no}.pdf?ref=${b.reference}`} target="_blank" rel="noopener noreferrer" title="View invoice PDF" className={ICON_BTN} data-testid={`pdf-${b.reference}`}><FileDown className="w-4 h-4" /></a>
                        )}
                        <button onClick={() => cancel(b)} title="Cancel booking (notifies waitlist)" className={`${ICON_BTN} !text-red-700`} data-testid={`cancel-${b.reference}`}><XCircle className="w-4 h-4" /></button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 mt-6">
        <div className="card-brutal p-4 shadow-[4px_4px_0px_0px_#0f172a] xl:col-span-2" data-testid="block-calendar">
          <div className="font-mono-tight text-xs uppercase tracking-[0.25em] text-slate-500 mb-1">Calendar — click a day or session to close / open it</div>
          <p className="text-xs text-slate-500 mb-3">Red = closed (hidden from teachers when the whole day is closed). Amber shows pupils booked.</p>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse min-w-[640px]">
              <thead>
                <tr className="font-mono-tight text-[10px] uppercase tracking-widest text-slate-500">
                  {["Mon", "Tue", "Wed", "Thu", "Fri"].map((d) => <th key={d} className="p-1 text-left">{d}</th>)}
                </tr>
              </thead>
              <tbody>
                {weeks.map(([monday, wk]) => (
                  <tr key={monday}>
                    {[1, 2, 3, 4, 5].map((wd) => {
                      const d = wk[wd];
                      if (!d) return <td key={wd} className="p-1"></td>;
                      const dayBlock = blockMap[`${d}|day`];
                      return (
                        <td key={wd} className={`p-1 align-top border-2 ${dayBlock ? "border-red-400 bg-red-50" : "border-slate-200"}`}>
                          <button
                            onClick={() => canEdit && toggleDay(d)}
                            className={`w-full text-left font-mono-tight text-[10px] uppercase tracking-widest font-bold px-1.5 py-1 border-2 transition-colors ${dayBlock ? "bg-red-300 border-slate-900" : "bg-white border-slate-300 hover:border-slate-900"}`}
                            data-testid={`cal-day-${d}`}
                            title={dayBlock ? "Day closed — click to open" : "Click to close the whole day"}
                          >
                            {format(new Date(`${d}T00:00:00`), "d MMM")}{dayBlock ? " · Closed" : ""}
                          </button>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {times.map((t) => {
                              const blk = dayBlock || blockMap[`${d}|${t}`];
                              const used = usage[`${d}|${t}`] || 0;
                              return (
                                <button
                                  key={t}
                                  onClick={() => canEdit && toggleSlot(d, t)}
                                  disabled={!canEdit}
                                  className={`font-mono-tight text-[10px] font-bold px-1.5 py-1 border-2 transition-colors ${blk ? "bg-red-300 border-slate-900" : used > 0 ? "bg-amber-200 border-slate-400 hover:border-slate-900" : "bg-emerald-100 border-slate-300 hover:border-slate-900"}`}
                                  data-testid={`cal-slot-${d}-${t.replace(":", "")}`}
                                  title={`${t} — ${used}/${cfg?.capacity ?? 75} booked${blk ? " (closed — click to open)" : " (click to close)"}`}
                                >
                                  {t}{used > 0 ? ` · ${used}` : ""}
                                </button>
                              );
                            })}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="card-brutal p-4 shadow-[4px_4px_0px_0px_#0f172a] h-fit" data-testid="waitlist-panel">
          <div className="font-mono-tight text-xs uppercase tracking-[0.25em] text-slate-500 mb-2">Waitlist ({waitlist.length})</div>
          {waitlist.length === 0 && <p className="text-xs text-slate-500">Waitlist is empty.</p>}
          {waitlist.map((w) => (
            <div key={w.id} className="flex items-center justify-between border-t border-slate-200 py-1.5 text-sm" data-testid={`waitlist-row-${w.id}`}>
              <span><b>{format(new Date(`${w.date}T00:00:00`), "EEE d MMM")} {w.time}</b> — {w.school_name} ({w.teacher_name}, {w.group_size} pupils){w.notified && <span className="ml-1 bg-emerald-200 border border-slate-900 px-1 text-[10px] uppercase font-bold">Notified</span>}</span>
              {canEdit && <button onClick={() => act(() => api.delete(`/school/admin/waitlist/${w.id}`), "Removed from waitlist")} className="text-red-700 font-bold text-xs uppercase hover:underline" data-testid={`waitlist-del-${w.id}`}>Remove</button>}
            </div>
          ))}
        </div>
      </div>

      {preview && (
        <div className="fixed inset-0 bg-slate-900/60 flex items-center justify-center p-4 z-50" data-testid="email-preview-modal">
          <div className="card-brutal bg-white p-6 w-full max-w-3xl max-h-[92vh] overflow-y-auto shadow-[6px_6px_0px_0px_#0f172a]">
            {preview.type === "invoice" ? (
              <>
                <h3 className="font-display text-xl uppercase mb-1">Invoice preview — {preview.invoice_no}</h3>
                <p className="text-xs text-slate-500 mb-3">Nothing has been sent yet. Review the PDF, then send.</p>
                <iframe
                  src={`${process.env.REACT_APP_BACKEND_URL}/api/school/invoice/${preview.invoice_no}.pdf?ref=${preview.bookings[0].reference}`}
                  className="w-full h-[420px] border-2 border-slate-900 mb-3 bg-white"
                  title="Invoice PDF preview"
                  data-testid="invoice-pdf-frame"
                />
                <div className="flex justify-between font-bold border-t-2 border-slate-900 pt-2 mb-2" data-testid="merge-total">
                  <span>Total ({preview.bookings.length} booking{preview.bookings.length > 1 ? "s" : ""})</span>
                  <span>{euro(preview.bookings.reduce((a, b) => a + b.group_size * price, 0))}</span>
                </div>
                <p className="text-xs text-slate-600 mb-4">Will be emailed to: {[...new Set(preview.bookings.map((b) => b.email))].join(", ")}</p>
              </>
            ) : (
              <>
                <h3 className="font-display text-xl uppercase mb-1">Reminder email preview</h3>
                <p className="text-xs text-slate-500 mb-3">Nothing has been sent yet. Review, then send.</p>
                <div className="border-2 border-slate-900 p-4 text-sm mb-4 bg-slate-50" data-testid="reminder-preview-body">
                  <p className="font-bold mb-2">Subject: {cfg?.label} booking reminder — {preview.bookings[0].reference}</p>
                  <p>Reminder: your school skating session at {cfg?.label} is coming up.</p>
                  <p className="mt-2"><b>{preview.bookings[0].date.split("-").reverse().join("-")} at {preview.bookings[0].time}</b> — {preview.bookings[0].group_size} pupils ({preview.bookings[0].school_name}).</p>
                  <p className="mt-2">Booking reference: <b>{preview.bookings[0].reference}</b></p>
                  <p className="mt-2"><a href={cfg?.info_pack} target="_blank" rel="noopener noreferrer" className="underline text-sky-700 font-bold" data-testid="reminder-preview-booklet-link">Download the Full Information Booklet (PDF)</a></p>
                  <p className="mt-3 font-bold">Important information for your visit:</p>
                  <ol className="list-decimal ml-5 mt-1 space-y-1">
                    <li><b>Arrival:</b> We recommend you ensure to be at the Ice Rink at least 15 minutes prior to your skating session starting. It will take time to get skates distributed to your group and go over all safety aspects.</li>
                    <li>Bring a spare pair of socks.</li>
                    <li>Bring tracksuit bottoms as there is a possibility of getting wet on the ice.</li>
                    <li>We are recommending all kids wear a helmet (not mandatory) — please bring a bike helmet, or we have a small number available free of charge to schools.</li>
                    <li>Ensure to notify us of the total number of kids who attended on the day and we will only invoice your school for that number.</li>
                  </ol>
                </div>
                <p className="text-xs text-slate-600 mb-4">Will be emailed to: {preview.bookings[0].email}</p>
              </>
            )}
            <div className="flex gap-2">
              <button onClick={confirmSend} className="flex-1 bg-slate-900 text-white border-2 border-slate-900 font-bold uppercase text-xs px-4 py-2 tracking-wider hover:bg-slate-700 transition-colors" data-testid="preview-send-btn">
                {preview.type === "reminder" ? "Send reminder email" : preview.bookings.length > 1 ? "Send merged invoice" : "Send invoice email"}
              </button>
              <button onClick={() => setPreview(null)} className="bg-white border-2 border-slate-900 font-bold uppercase text-xs px-4 py-2 tracking-wider hover:bg-slate-100 transition-colors" data-testid="preview-cancel-btn">Back</button>
            </div>
          </div>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 bg-slate-900/60 flex items-center justify-center p-4 z-50" data-testid="edit-modal">
          <div className="card-brutal bg-white p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-[6px_6px_0px_0px_#0f172a]">
            <h3 className="font-display text-xl uppercase mb-4">Edit {editing.reference}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <label className="text-sm font-bold">Date <span className="text-red-600">*</span>
                <select className="input-brutal mt-1 w-full" value={editing.date} onChange={(e) => setEditing((f) => ({ ...f, date: e.target.value }))} data-testid="edit-date">
                  {days.map((d) => <option key={d} value={d}>{format(new Date(`${d}T00:00:00`), "EEE d MMM yyyy")}</option>)}
                </select>
              </label>
              <label className="text-sm font-bold">Time <span className="text-red-600">*</span>
                <select className="input-brutal mt-1 w-full" value={editing.time} onChange={(e) => setEditing((f) => ({ ...f, time: e.target.value }))} data-testid="edit-time">
                  {times.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </label>
              {[["school_name", "School name"], ["teacher_name", "Teacher name"], ["email", "Email"], ["phone", "Phone"], ["county", "County"], ["po_number", "PO number"]].map(([k, label]) => (
                <label key={k} className="text-sm font-bold">{label}{k !== "po_number" && <span className="text-red-600"> *</span>}
                  <input className="input-brutal mt-1 w-full" value={editing[k] || ""} onChange={(e) => setEditing((f) => ({ ...f, [k]: e.target.value }))} data-testid={`edit-${k}`} />
                </label>
              ))}
              <label className="text-sm font-bold">Age group <span className="text-red-600">*</span>
                {cfg?.age_groups ? (
                  <select className="input-brutal mt-1 w-full" value={editing.age_group} onChange={(e) => setEditing((f) => ({ ...f, age_group: e.target.value }))} data-testid="edit-age-group">
                    {[...cfg.age_groups, ...(editing.age_group && !cfg.age_groups.includes(editing.age_group) ? [editing.age_group] : [])].map((a) => <option key={a} value={a}>{a}</option>)}
                  </select>
                ) : (
                  <input className="input-brutal mt-1 w-full" value={editing.age_group || ""} onChange={(e) => setEditing((f) => ({ ...f, age_group: e.target.value }))} data-testid="edit-age-group" />
                )}
              </label>
              <label className="text-sm font-bold">Pupils <span className="text-red-600">*</span>
                <input type="number" min="1" className="input-brutal mt-1 w-full" value={editing.group_size} onChange={(e) => setEditing((f) => ({ ...f, group_size: e.target.value }))} data-testid="edit-group-size" />
              </label>
              <label className="text-sm font-bold">Payment method <span className="text-red-600">*</span>
                <select className="input-brutal mt-1 w-full" value={editing.payment_method} onChange={(e) => setEditing((f) => ({ ...f, payment_method: e.target.value }))} data-testid="edit-payment-method">
                  {(cfg?.payment_methods || []).map((pm) => <option key={pm.value} value={pm.value}>{pm.label}</option>)}
                </select>
              </label>
              <label className="text-sm font-bold">Status <span className="text-red-600">*</span>
                <select className="input-brutal mt-1 w-full" value={editing.status} onChange={(e) => setEditing((f) => ({ ...f, status: e.target.value }))} data-testid="edit-status">
                  {[...STATUSES, ...(STATUSES.includes(editing.status) ? [] : [editing.status])].map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </label>
              <label className="text-sm font-bold md:col-span-2">School address <span className="text-red-600">*</span>
                <textarea className="input-brutal mt-1 w-full" rows={2} value={editing.school_address || ""} onChange={(e) => setEditing((f) => ({ ...f, school_address: e.target.value }))} data-testid="edit-school-address" />
              </label>
              <label className="text-sm font-bold md:col-span-2">Teacher notes
                <textarea className="input-brutal mt-1 w-full" rows={2} value={editing.notes || ""} onChange={(e) => setEditing((f) => ({ ...f, notes: e.target.value }))} data-testid="edit-notes" />
              </label>
              <label className="text-sm font-bold md:col-span-2">Admin notes (internal)
                <textarea className="input-brutal mt-1 w-full !bg-amber-50" rows={2} value={editing.admin_notes || ""} onChange={(e) => setEditing((f) => ({ ...f, admin_notes: e.target.value }))} data-testid="edit-admin-notes" />
              </label>
            </div>
            <div className="flex gap-2 mt-4">
              {canEdit && <button onClick={saveEdit} className="flex-1 bg-slate-900 text-white border-2 border-slate-900 font-bold uppercase text-xs px-4 py-2 tracking-wider hover:bg-slate-700 transition-colors" data-testid="edit-save">Save changes</button>}
              <button onClick={() => setEditing(null)} className="bg-white border-2 border-slate-900 font-bold uppercase text-xs px-4 py-2 tracking-wider hover:bg-slate-100 transition-colors" data-testid="edit-close">Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
